import { AICodeAuthorProvider } from '@/types';
import { displayNameForAICodeAuthorProvider } from '../featureReadiness/aiCoAuthorDisplayHelpers';
import { antGrey } from '../styles/globalStyles';
import { RECHARTS_DEFAULT_SERIES_COLORS } from './OrgOverviewRecharts';

const AI_AUTHOR_PROVIDER_ORDER = [
    AICodeAuthorProvider.Claude,
    AICodeAuthorProvider.Cursor,
    AICodeAuthorProvider.Copilot,
];

// One base hue per provider, drawn from the same palette as other org overview stacked charts.
const PROVIDER_BASE_COLOR_INDICES = [0, 2, 4];

const MIN_MODEL_SHADE_WHITE_MIX = 0.08;
const MAX_MODEL_SHADE_WHITE_MIX = 0.42;

export const formatAIAuthorDistributionSegmentLabel = (provider: AICodeAuthorProvider, name: string): string =>
    `${displayNameForAICodeAuthorProvider(provider)} (${name})`;

export const compareAIAuthorDistributionSegments = (
    left: { provider: AICodeAuthorProvider; name: string },
    right: { provider: AICodeAuthorProvider; name: string },
): number => {
    const leftProviderOrder = AI_AUTHOR_PROVIDER_ORDER.indexOf(left.provider);
    const rightProviderOrder = AI_AUTHOR_PROVIDER_ORDER.indexOf(right.provider);
    if (leftProviderOrder !== rightProviderOrder) {
        return leftProviderOrder - rightProviderOrder;
    }

    return left.name.localeCompare(right.name, undefined, { sensitivity: 'base' });
};

export const sortAIAuthorDistributionCategoryLabels = (
    categories: string[],
    providerByCategory: Record<string, AICodeAuthorProvider | undefined>,
): string[] =>
    [...categories].sort((left, right) => {
        const leftProvider = providerByCategory[left];
        const rightProvider = providerByCategory[right];
        const leftProviderOrder =
            leftProvider != null ? AI_AUTHOR_PROVIDER_ORDER.indexOf(leftProvider) : AI_AUTHOR_PROVIDER_ORDER.length;
        const rightProviderOrder =
            rightProvider != null ? AI_AUTHOR_PROVIDER_ORDER.indexOf(rightProvider) : AI_AUTHOR_PROVIDER_ORDER.length;

        if (leftProviderOrder !== rightProviderOrder) {
            return leftProviderOrder - rightProviderOrder;
        }

        return left.localeCompare(right, undefined, { sensitivity: 'base' });
    });

function parseHexColor(hex: string): [number, number, number] {
    const normalized = hex.replace('#', '');
    return [
        Number.parseInt(normalized.slice(0, 2), 16),
        Number.parseInt(normalized.slice(2, 4), 16),
        Number.parseInt(normalized.slice(4, 6), 16),
    ];
}

function toHexByte(value: number) {
    return Math.round(value).toString(16).padStart(2, '0');
}

function mixHexColors(left: string, right: string, rightWeight: number): string {
    const [leftR, leftG, leftB] = parseHexColor(left);
    const [rightR, rightG, rightB] = parseHexColor(right);
    const leftWeight = 1 - rightWeight;

    return `#${toHexByte(leftR * leftWeight + rightR * rightWeight)}${toHexByte(leftG * leftWeight + rightG * rightWeight)}${toHexByte(leftB * leftWeight + rightB * rightWeight)}`;
}

function getProviderBaseColor(provider: AICodeAuthorProvider, seriesPalette: string[]): string {
    const providerOrder = AI_AUTHOR_PROVIDER_ORDER.indexOf(provider);
    const paletteIndex = PROVIDER_BASE_COLOR_INDICES[providerOrder] ?? providerOrder;

    return seriesPalette[paletteIndex % seriesPalette.length];
}

function colorForProviderModel(
    provider: AICodeAuthorProvider | undefined,
    modelIndex: number,
    modelCount: number,
    seriesPalette: string[],
): string {
    if (!provider) {
        return antGrey.grey5;
    }

    const baseColor = getProviderBaseColor(provider, seriesPalette);
    if (modelCount <= 1) {
        return baseColor;
    }

    const shadeProgress = modelIndex / (modelCount - 1);
    const whiteMix =
        MIN_MODEL_SHADE_WHITE_MIX + shadeProgress * (MAX_MODEL_SHADE_WHITE_MIX - MIN_MODEL_SHADE_WHITE_MIX);

    return mixHexColors(baseColor, '#ffffff', whiteMix);
}

export const buildAIAuthorDistributionColorByCategory = (
    categories: string[],
    providerByCategory: Record<string, AICodeAuthorProvider | undefined>,
    seriesPalette: string[] = RECHARTS_DEFAULT_SERIES_COLORS,
): Record<string, string> => {
    const palette = seriesPalette.length ? seriesPalette : RECHARTS_DEFAULT_SERIES_COLORS;
    const modelCountByProvider: Record<string, number> = {};
    const providerIndices: Record<string, number> = {};
    const colorByCategory: Record<string, string> = {};

    categories.forEach((category) => {
        const provider = providerByCategory[category];
        const paletteKey = provider ?? 'none';
        modelCountByProvider[paletteKey] = (modelCountByProvider[paletteKey] ?? 0) + 1;
    });

    categories.forEach((category) => {
        const provider = providerByCategory[category];
        const paletteKey = provider ?? 'none';
        const modelIndex = providerIndices[paletteKey] ?? 0;
        providerIndices[paletteKey] = modelIndex + 1;
        colorByCategory[category] = colorForProviderModel(
            provider,
            modelIndex,
            modelCountByProvider[paletteKey] ?? 1,
            palette,
        );
    });

    return colorByCategory;
};
